import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  ClientDomain,
  ClientDomainAvailability,
  ClientDomainInstructions,
  ClientDomainPricing,
} from './../../models/ClientDomain';
import { PaginatedResult } from './../model/paginated-result';

@Injectable({ providedIn: 'root' })
export class ClientDomainService {

  constructor(private http: HttpClient) { }

  list(): Observable<ClientDomain[]> {
    return this.http.get<ClientDomain[]>(`${environment.apiUrl}/client-domains`);
  }

  availability(domain: string, type: string): Observable<ClientDomainAvailability> {
    return this.http.get<ClientDomainAvailability>(
      `${environment.apiUrl}/client-domains/availability?domain=${encodeURIComponent(domain)}&type=${type}`
    );
  }

  create(domain: string, type: string, userId?: string, allowWithoutPlan?: boolean): Observable<{ domain: ClientDomain, instructions: ClientDomainInstructions }> {
    const body: any = { domain, type };

    if (userId) {
      body.user_id = userId;
    }

    if (allowWithoutPlan) {
      body.allow_without_plan = true;
    }

    return this.http.post<{ domain: ClientDomain, instructions: ClientDomainInstructions }>(
      `${environment.apiUrl}/client-domains`,
      body
    );
  }

  adminList(page = 1, perPage = 25): Observable<PaginatedResult<ClientDomain[]>> {
    return this.http.get<PaginatedResult<ClientDomain[]>>(
      `${environment.apiUrl}/admin/client-domains?page=${page}&per_page=${perPage}`
    );
  }

  getById(id: string): Observable<{ domain: ClientDomain, instructions: ClientDomainInstructions, renewal_pricing?: ClientDomainPricing, transfer_price?: number }> {
    return this.http.get<{ domain: ClientDomain, instructions: ClientDomainInstructions, renewal_pricing?: ClientDomainPricing, transfer_price?: number }>(
      `${environment.apiUrl}/client-domains/${id}`
    );
  }

  requestTransfer(id: string, payment: any): Observable<{ recharge: any, domain: ClientDomain }> {
    return this.http.post<{ recharge: any, domain: ClientDomain }>(
      `${environment.apiUrl}/client-domains/${id}/request-transfer`,
      payment
    );
  }

  confirmTransfer(id: string): Observable<ClientDomain> {
    return this.http.put<ClientDomain>(`${environment.apiUrl}/admin/client-domains/${id}/confirm-transfer`, {});
  }

  renew(id: string, payment: any): Observable<{ recharge: any, domain: ClientDomain }> {
    return this.http.post<{ recharge: any, domain: ClientDomain }>(
      `${environment.apiUrl}/client-domains/${id}/renew`,
      payment
    );
  }

  verifyOwnership(id: string): Observable<ClientDomain> {
    return this.http.post<ClientDomain>(`${environment.apiUrl}/client-domains/${id}/verify-ownership`, {});
  }

  checkout(id: string, payment: any): Observable<{ recharge: any, domain: ClientDomain }> {
    return this.http.post<{ recharge: any, domain: ClientDomain }>(
      `${environment.apiUrl}/client-domains/${id}/checkout`,
      payment
    );
  }

  verifyDns(id: string): Observable<ClientDomain> {
    return this.http.post<ClientDomain>(`${environment.apiUrl}/client-domains/${id}/verify-dns`, {});
  }

  cancel(id: string): Observable<ClientDomain> {
    return this.http.delete<ClientDomain>(`${environment.apiUrl}/client-domains/${id}`);
  }

  confirmInfra(id: string): Observable<ClientDomain> {
    return this.http.put<ClientDomain>(`${environment.apiUrl}/admin/client-domains/${id}/confirm-infra`, {});
  }

  adminCancel(id: string, reason?: string): Observable<ClientDomain> {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';

    return this.http.delete<ClientDomain>(`${environment.apiUrl}/admin/client-domains/${id}${query}`);
  }
}
